import { useState } from "react";
import { Share2, Check } from "lucide-react";
import { toast } from "sonner";

/**
 * Smart share button.
 *  - Mobile / supported browsers: native share sheet (WhatsApp, Messages, IG, etc.)
 *  - Desktop / fallback: copies URL to clipboard
 *
 * Props:
 *  - title: string, share title (also used as page context)
 *  - text:  string, share preview text
 *  - url:   optional url; defaults to window.location.href
 *  - variant: "outline" (default) | "solid"
 *  - testid
 */
export default function ShareButton({ title, text, url, variant = "outline", testid = "share-button" }) {
  const [copied, setCopied] = useState(false);

  const share = async () => {
    const shareUrl = url || (typeof window !== "undefined" ? window.location.href : "");
    const payload = { title, text, url: shareUrl };

    if (typeof navigator !== "undefined" && navigator.share) {
      try {
        await navigator.share(payload);
        return;
      } catch (err) {
        // user cancelled or share failed — fall through to clipboard
        if (err && err.name === "AbortError") return;
      }
    }

    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Link copied — paste it anywhere");
      setTimeout(() => setCopied(false), 2200);
    } catch {
      toast.error("Couldn't copy — long-press the address bar to share.");
    }
  };

  const styles = variant === "solid"
    ? "bg-[var(--ink)] text-[var(--bg)] hover:bg-[var(--forest)]"
    : "border border-[var(--line)] text-[var(--ink)] hover:border-[var(--terracotta)] hover:text-[var(--forest)]";

  return (
    <button
      type="button"
      onClick={share}
      data-testid={testid}
      className={`inline-flex items-center gap-2 ${styles} px-5 py-2.5 rounded-full text-sm transition-colors`}
    >
      {copied ? <Check className="w-4 h-4" /> : <Share2 className="w-4 h-4" />}
      {copied ? "Copied" : "Share this page"}
    </button>
  );
}
